import { Transaction } from '../types';

/**
 * Normaliza la URL del PDF que devuelve el backend.
 * El servidor a veces devuelve: "https://kaoxet.net/data:application/pdf;base64,..."
 * cuando en realidad el contenido útil es: "data:application/pdf;base64,..."
 */
function normalizePdfUrl(url: string): string {
  // Buscar si hay un data URI embebido después del dominio
  const dataIdx = url.indexOf('/data:');
  if (dataIdx !== -1) return url.substring(dataIdx + 1); // "data:application/pdf;base64,..."
  return url;
}

/**
 * Abre un PDF ya sea desde:
 *   - data:application/pdf;base64,... → convierte a Blob y abre
 *   - https://... → abre directamente en pestaña nueva
 */
function openPdf(rawUrl: string): void {
  const url = normalizePdfUrl(rawUrl);
  if (url.startsWith('data:')) {
    // Es un base64 — crear Blob URL para no sobrepasar el límite de URL
    try {
      const [header, base64] = url.split(',');
      const mime = header.replace('data:', '').replace(';base64', '');
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank', 'width=800,height=900');
      if (!win) {
        // Fallback: descarga directa
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'comprobante.pdf';
        a.click();
      }
      // Limpiar Blob URL después de 60 s
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      console.error('Error al abrir PDF base64:', e);
      alert('No se pudo abrir el comprobante. Intente nuevamente.');
    }
  } else {
    const win = window.open(url, '_blank', 'width=800,height=900');
    if (!win) alert('Por favor permita las ventanas emergentes para ver el comprobante');
  }
}

/**
 * Servicio para manejar la impresión / descarga de tickets
 */
export const printService = {
  printTicket: (transaction: Transaction): void => {
    // Si el servidor devolvió un PDF (base64 o URL), usarlo directamente
    if (transaction.url) {
      openPdf(transaction.url);
      return;
    }
    // Fallback: generar ticket localmente
    printService.printLocalTicket(transaction);
  },

  printLocalTicket: (transaction: Transaction): void => {
    const printWindow = window.open('', '_blank', 'width=400,height=700');
    if (!printWindow) {
      alert('Por favor permita las ventanas emergentes para imprimir el ticket');
      return;
    }

    const details = transaction.details as any;
    const date = new Date(transaction.date).toLocaleString('es-DO');

    const ticketHTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Ticket de Transacción</title>
  <style>
    body { font-family: 'Courier New', monospace; width: 80mm; margin: 0 auto; padding: 5mm; text-align: center; font-size: 12px; }
    .logo  { font-size: 20px; font-weight: bold; margin-bottom: 4px; }
    .title { font-size: 14px; font-weight: bold; margin: 8px 0; text-transform: uppercase; }
    .info  { text-align: left; margin: 4px 0; }
    .item  { display: flex; justify-content: space-between; margin: 2px 0; }
    .divider { border-top: 1px dashed #000; margin: 8px 0; }
    .footer  { margin-top: 8px; font-size: 10px; }
    @media print { body { width: 80mm; margin: 0; padding: 0; } @page { size: 80mm auto; margin: 0; } }
  </style>
</head>
<body>
  <div class="logo">Pay+</div>
  <div>Sistema de pagos y recargas</div>
  <div>${date}</div>
  <div class="divider"></div>
  <div class="title">
    ${transaction.type === 'RECARGA' ? 'RECARGA ELECTRÓNICA' :
      transaction.type === 'PAQUETE' ? 'COMPRA DE PAQUETE'   :
      transaction.type === 'FACTURA' ? 'PAGO DE FACTURA'      : 'COMPRA DE SEGURO'}
  </div>
  <div class="divider"></div>
  <div class="info">
    ${transaction.type === 'RECARGA' ? `
      <div class="item"><span>Compañía:</span><span>${transaction.provider}</span></div>
      <div class="item"><span>Número:</span><span>${details.phoneNumber || ''}</span></div>
    ` : transaction.type === 'PAQUETE' ? `
      <div class="item"><span>Compañía:</span><span>${transaction.provider}</span></div>
      <div class="item"><span>Número:</span><span>${details.phoneNumber || ''}</span></div>
      <div class="item"><span>Paquete:</span><span>${details.packageName || ''}</span></div>
    ` : transaction.type === 'FACTURA' ? `
      <div class="item"><span>Servicio:</span><span>${transaction.provider}</span></div>
      <div class="item"><span>Número:</span><span>${details.serviceNumber || ''}</span></div>
      ${details.customerName ? `<div class="item"><span>Cliente:</span><span>${details.customerName}</span></div>` : ''}
    ` : `
      <div class="item"><span>Plan:</span><span>${details.planName || ''}</span></div>
      <div class="item"><span>Teléfono:</span><span>${details.phoneNumber || details.customerPhone || ''}</span></div>
    `}
    <div class="divider"></div>
    <div class="item"><span>Monto:</span><span>RD$ ${Number(transaction.amount).toFixed(2)}</span></div>
    <div class="item"><span>Referencia:</span><span>${transaction.reference}</span></div>
    <div class="item"><span>ID:</span><span>${transaction.xid || transaction.id}</span></div>
  </div>
  <div class="divider"></div>
  <div>¡Transacción exitosa!</div>
  <div class="footer">
    <div>Gracias por usar nuestros servicios</div>
    <div>PayPlus — Tus Pagos más Fácil</div>
  </div>
</body>
</html>`;

    printWindow.document.write(ticketHTML);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
  }
};
